/* istanbul ignore file : ticket https://heycar.atlassian.net/browse/MTEC-1008 */
/* eslint-disable react/no-multi-comp */
/* eslint-disable react-perf/jsx-no-new-function-as-prop,react/display-name */
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { browserHistory } from 'react-router';

import { Button, Link, LinkButton } from 'app/shared/ui';
import {
  COOKIE_DASHBOARD_HREF,
  COOKIE_DASHBOARD_LINK,
  MORE_DETAILS_HREF,
  MORE_DETAILS_LINK,
} from 'app/shared/components/CookieBanner/constants';
import {
  CookieBannerCallToActionSt,
  CookieBannerContainerSt,
  CookieBannerContentSt,
  CookieBannerOverlaySt,
  CookieBannerTextWrapperSt,
} from 'app/shared/components/CookieBanner/CookieBanner.css';
import { useStyledTheme } from 'app/shared/hooks/useStyledTheme';
import { trackAction } from 'app/shared/utils/tracking';
import { appendGptScriptsToHead } from 'app/shared/modules/Campaign/GAM/utils/utils';

export const CookieBanner = ({
  target,
  shouldNotRender,
  setHasCookieConsent,
  setRejectAllCookies,
  hasSavedCookieSettings,
  setHasSavedCookieSettings,
}) => {
  const { colors } = useStyledTheme();
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const hideBanner = hasSavedCookieSettings || shouldNotRender;

  useEffect(() => {
    setShowCookieBanner(true);
  }, []);

  useEffect(() => {
    window.document.body.style.overflow = 'hidden';
  }, []);

  useEffect(() => {
    if (hideBanner) window.document.body.style.overflow = 'scroll';
  }, [hideBanner]);

  if (hideBanner) return null;

  const navigateToHref = (href) => {
    browserHistory.push(href);
  };

  // eslint-disable-next-line consistent-return
  const catchAnchorClick = (event) => {
    event.preventDefault();
    const { id } = event.target;

    switch (id) {
      case MORE_DETAILS_LINK:
        trackAction('cookie_banner_more_details_link_click', {
          category: target,
          description: 'user clicks on the more-details link in the copy',
        });

        navigateToHref(MORE_DETAILS_HREF);
        break;

      case COOKIE_DASHBOARD_LINK:
        trackAction('cookie_banner_dashboard_link_click', {
          category: target,
          description: 'user clicks on the cookie dashboard link in the copy',
        });

        navigateToHref(COOKIE_DASHBOARD_HREF);
        break;
      default:
        return null;
    }
  };

  const acceptCookies = async (event) => {
    /**
     * Append the gpt script to the `head` when the marketing cookie consent given.
     * This will allow us to render GAM banners smoothly.
     */
    await appendGptScriptsToHead();

    trackAction('cookie_banner_accept_cta_click', {
      category: target,
      description: 'user clicks on the cookie banner cta',
    });

    setHasCookieConsent();
    setHasSavedCookieSettings();
    event.stopPropagation();
  };

  const onRejectCookies = (event) => {
    setRejectAllCookies();
    setHasSavedCookieSettings();
    event.stopPropagation();
  };

  const handleMoreDetailsClick = () => {
    trackAction('cookie_banner_show_details_cta_click', {
      category: target,
      description: 'user clicks on the more-details link',
    });

    navigateToHref(COOKIE_DASHBOARD_HREF);
  };

  const handleMoreDetailsLinkClick = () => {
    trackAction('cookie_banner_more_details_link_click', {
      category: target,
      description: 'user clicks on the more-details link',
    });
  };

  return (
    <>
      <CookieBannerOverlaySt />
      {
        <CookieBannerContainerSt onClick={catchAnchorClick}>
          <CookieBannerContentSt>
            <CookieBannerTextWrapperSt>
              <p>
                Mit Klick auf &quot;Akzeptieren&quot; erteilst du uns deine
                Einwilligung, dass wir deine Nutzung unserer Plattform durch
                Cookies und andere Technologien (von heycar und Dritten)
                nachverfolgen, um deine Nutzungserfahrung und dir angezeigte
                Werbung zu analysieren und personalisieren (
                <Link
                  to={MORE_DETAILS_HREF}
                  color={colors.mintDark}
                  hoverColor={colors.brandPrimary}
                  onClick={handleMoreDetailsLinkClick}
                >
                  <strong id={MORE_DETAILS_LINK}>mehr Details</strong>
                </Link>
                ).
              </p>
              <p>
                Du stimmst damit auch zu, dass deine Daten hierbei ohne
                geeignete Datenschutzgarantien an Partner in sog. Drittländern
                übermittelt werden können, die kein angemessenes
                Datenschutzniveau bieten, wie z.B. die USA aufgrund staatlicher
                Zugriffsmöglichkeiten.
              </p>
              <p>
                Alternativ kannst du deine Einstellungen im{' '}
                <Link
                  to={COOKIE_DASHBOARD_HREF}
                  color={colors.mintDark}
                  hoverColor={colors.brandPrimary}
                >
                  <strong id={COOKIE_DASHBOARD_LINK}>Cookie Richtlinie</strong>
                </Link>{' '}
                jetzt und jederzeit später individuell anpassen. Willst du{' '}
                <strong>keine Einwilligung</strong> erteilen, dann klick bitte{' '}
                <LinkButton
                  as="button"
                  variant="tertiary"
                  onClick={onRejectCookies}
                  disabled={!showCookieBanner}
                  // rest
                  p={0}
                  style={{ transition: 'none' }}
                >
                  <strong>hier</strong>
                </LinkButton>
                .
              </p>
            </CookieBannerTextWrapperSt>
            <CookieBannerCallToActionSt>
              <LinkButton
                to={COOKIE_DASHBOARD_HREF}
                onClick={handleMoreDetailsClick}
                variant="secondary"
                width={1}
                disabled={!showCookieBanner}
              >
                Details anzeigen
              </LinkButton>
              <Button
                width={1}
                onClick={acceptCookies}
                data-testid="shared-cookieBanner-acceptBtn"
                disabled={!showCookieBanner}
              >
                Akzeptieren
              </Button>
            </CookieBannerCallToActionSt>
          </CookieBannerContentSt>
        </CookieBannerContainerSt>
      }
    </>
  );
};

CookieBanner.propTypes = {
  hasSavedCookieSettings: PropTypes.bool.isRequired,
  setHasCookieConsent: PropTypes.func.isRequired,
  setRejectAllCookies: PropTypes.func.isRequired,
  setHasSavedCookieSettings: PropTypes.func.isRequired,
  shouldNotRender: PropTypes.bool.isRequired,
  target: PropTypes.string,
};

CookieBanner.defaultProps = {
  target: null,
};
